import { useState, useEffect } from 'react';
import { buildUrl, default as API_CONFIG } from '../config/api';

/**
 * Single-user auth: POST {username, password} to /api/auth/login. Backend
 * validates against env-var-backed credentials and returns a function key
 * that subsequent API calls attach as `x-functions-key`.
 *
 * Common failure modes and the messages this hook surfaces -- diagnosing
 * "Failed to fetch" required staring at a generic browser error long
 * enough that the hook now tries to be helpful about WHY a fetch failed:
 *
 *   - Unreachable / wrong base URL  -> "Cannot reach API at <url>. Check
 *                                       REACT_APP_API_URL is set on the
 *                                       static web app, and the function
 *                                       app's CORS allowed origins include
 *                                       this site."
 *   - 401 / 403                     -> "Invalid credentials"
 *   - 4xx / 5xx with JSON body      -> the body's `error` field
 *   - Anything else                 -> the raw error message
 */
export const useSimpleAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [functionKey, setFunctionKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedKey = localStorage.getItem('azure_function_key');
    const storedUser = localStorage.getItem('user_info');

    if (storedKey) {
      setFunctionKey(storedKey);
      setIsAuthenticated(true);
      if (storedUser) {
        try { setUser(JSON.parse(storedUser)); } catch { /* ignore corrupt user blob */ }
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    // Use API_CONFIG.baseUrl as the single source of truth -- it falls
    // back to localhost:7071 in dev when REACT_APP_API_URL is unset,
    // instead of synthesizing the malformed string "undefined/auth/login"
    // we used to produce by reading process.env directly.
    const url = buildUrl('/auth/login');

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
    } catch (err) {
      // Browser fetch only throws on network-level errors -- DNS failure,
      // CORS rejection of preflight, offline. Status codes don't throw.
      const msg = (err?.message || '').toLowerCase();
      const isNetwork = msg.includes('failed to fetch') || msg.includes('networkerror');
      throw new Error(
        isNetwork
          ? `Cannot reach the API at ${API_CONFIG.baseUrl}. ` +
            `Check that REACT_APP_API_URL is configured on the static web app build, ` +
            `and that the function app's CORS allowed origins include this site.`
          : (err?.message || 'Network error during login'),
      );
    }

    if (!response.ok) {
      let backendMessage;
      try {
        const body = await response.json();
        backendMessage = body?.error || body?.message;
      } catch { /* not JSON; fall through to default */ }

      if (response.status === 401 || response.status === 403) {
        throw new Error(backendMessage || 'Invalid credentials');
      }
      throw new Error(
        backendMessage ||
        `Login failed (${response.status} ${response.statusText || ''}). ` +
        `Check the function app logs.`,
      );
    }

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('Login succeeded but the response was not JSON. Check the function app logs.');
    }

    const { functionKey: returnedKey, user: returnedUser } = data;
    if (!returnedKey) {
      throw new Error(
        'Login response missing functionKey. Check the FUNCTION_KEY app setting on the function app.',
      );
    }

    localStorage.setItem('azure_function_key', returnedKey);
    if (returnedUser) {
      localStorage.setItem('user_info', JSON.stringify(returnedUser));
      setUser(returnedUser);
    }
    setFunctionKey(returnedKey);
    setIsAuthenticated(true);
    return returnedKey;
  };

  const logout = () => {
    localStorage.removeItem('azure_function_key');
    localStorage.removeItem('user_info');
    setFunctionKey(null);
    setIsAuthenticated(false);
    setUser(null);
  };

  return {
    isAuthenticated,
    functionKey,
    user,
    login,
    logout,
    loading,
  };
};

export default useSimpleAuth;
